import { createSlice, PayloadAction, WritableDraft } from "@reduxjs/toolkit";

import { formatConversationMessage } from "./utils";

import type { DeserializedMessage } from "./utils";
import type { Message as IMessage } from "@regal-voice/shared-types";
import type { Conversation } from "@twilio/conversations";

export type ActiveConversationsState = {
    conversationMessages: { [sid: string]: Partial<IMessage>[] };
    reservationMessages: { [sid: string]: Partial<IMessage>[] };
    notififiedMessages: { [sid: string]: string[] };
    instances: Record<string, Conversation>;
};

const initialState: ActiveConversationsState = {
    conversationMessages: {},
    reservationMessages: {},
    notififiedMessages: {},
    instances: {},
};
/**
 * This helper is necessary because we are working with non-serializable data (Twilio Conversation
 * instances), and the WritableDraft type from immer causes type and performance issues when trying
 * to assign a new entity to a record within the draft state.
 *
 * @example
 * // Causes issues:
 * state.instances[sid] = conversationInstance;
 *
 * // Works fine:
 * unwrapDraftType(state.instances)[sid] = conversationInstance;
 */
const unwrapDraftType = <T>(draft: WritableDraft<T>): T => draft as unknown as T;

const conversationsSlice = createSlice({
    name: "activeConversations",
    initialState,
    reducers: {
        setConversation: (state, action: PayloadAction<{ conversationInstance: Conversation }>) => {
            const { conversationInstance } = action.payload;
            const sid = conversationInstance.sid;
            unwrapDraftType(state.instances)[sid] = conversationInstance;
        },
        updateConversation: (state, action: PayloadAction<{ conversationInstance: Conversation }>) => {
            const { conversationInstance } = action.payload;
            const sid = conversationInstance.sid;
            unwrapDraftType(state.instances)[sid] = conversationInstance;
        },
        removeConversation: (state, action: PayloadAction<{ conversationInstance: Conversation }>) => {
            const sid = action.payload.conversationInstance.sid;
            state.conversationMessages[sid] = [];
            state.reservationMessages[sid] = [];
            state.notififiedMessages[sid] = [];
            delete state.instances[sid];
        },
        setReservationMessages: (state, action: PayloadAction<{ sid: string; messages: any[] }>) => {
            if (state.reservationMessages[action.payload.sid] === undefined) {
                state.reservationMessages[action.payload.sid] = [];
            }
            const cleanMessages = action.payload.messages.map((m) => {
                return formatConversationMessage(m);
            });

            state.reservationMessages[action.payload.sid].push(...cleanMessages);
        },
        // this message any type is the output of deserializeConversationMessage.
        addConversationMessage: (state, action: PayloadAction<{ sid: string; message: DeserializedMessage }>) => {
            if (state.conversationMessages[action.payload.sid] === undefined) {
                state.conversationMessages[action.payload.sid] = [];
            }
            const cleanMessage = formatConversationMessage(action.payload.message);
            state.conversationMessages[action.payload.sid].push(cleanMessage);
        },
        addNotifiedMessage: (state, action: PayloadAction<{ sid: string; messageSid: string }>) => {
            if (state.notififiedMessages[action.payload.sid] === undefined) {
                state.notififiedMessages[action.payload.sid] = [];
            }
            state.notififiedMessages[action.payload.sid].push(action.payload.messageSid);
        },
    },
});

export const {
    setConversation,
    updateConversation,
    removeConversation,
    setReservationMessages,
    addConversationMessage,
    addNotifiedMessage,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
